import { useState } from 'react';
import { useI18n } from '@/i18n';
import { useTenant } from '@/contexts/TenantContext';
import {
  Badge,
  Button,
  Card,
  Spinner,
} from '@/components/ui';
import {
  useCACApproveSettlement,
  useCACDisputeSettlement,
  useCACMarkSettlementPaid,
  useCACRecalculateSettlement,
  useCACRevenueTransactions,
  useCACSettlements,
} from '@/features/cac';
import type { RevenueTransaction, Settlement, SettlementStatus } from '@/features/cac';

const STATUS_BADGE_CLASS: Record<SettlementStatus, string> = {
  draft: 'bg-gray-50 text-gray-600 border-gray-200',
  calculated: 'bg-blue-50 text-blue-700 border-blue-200',
  pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  disputed: 'bg-red-50 text-red-700 border-red-200',
  recalculated: 'bg-purple-50 text-purple-700 border-purple-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function publicErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Operation failed.';
  const maybeAxios = error as {
    response?: { data?: { detail?: { message?: string } } };
  };
  return maybeAxios.response?.data?.detail?.message ?? 'Operation failed. Please retry.';
}

export function RevenuePage() {
  const { t: _t } = useI18n();
  void _t;
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  const [disputeReason, setDisputeReason] = useState('');
  const [disputingId, setDisputingId] = useState<string | null>(null);
  const [payoutRef, setPayoutRef] = useState('');

  const transactionsQuery = useCACRevenueTransactions(tenantId);
  const settlementsQuery = useCACSettlements(tenantId);
  const approveMutation = useCACApproveSettlement(tenantId);
  const disputeMutation = useCACDisputeSettlement(tenantId);
  const recalculateMutation = useCACRecalculateSettlement();
  const markPaidMutation = useCACMarkSettlementPaid();

  const transactions = transactionsQuery.data ?? [];
  const settlements = settlementsQuery.data ?? [];

  const currentMonth = new Date();
  const monthTransactions = transactions.filter((tr: RevenueTransaction) => {
    const date = new Date(tr.created_at);
    return (
      date.getFullYear() === currentMonth.getFullYear() &&
      date.getMonth() === currentMonth.getMonth()
    );
  });
  const monthRevenue = monthTransactions.reduce((sum: number, tr: RevenueTransaction) => sum + tr.partner_share_cents, 0);
  const pendingSettlement = settlements.find((s: Settlement) => s.status === 'pending_review');
  const lastPaid = settlements.find((s: Settlement) => s.status === 'paid');

  const handleDispute = async (settlementId: string) => {
    await disputeMutation.mutateAsync({ settlementId, reason: disputeReason });
    setDisputingId(null);
    setDisputeReason('');
  };

  return (
    <div className="space-y-6" data-testid="revenue-page">
      <h2 className="text-lg font-semibold">Revenue & Settlements</h2>

      {(disputeMutation.isError || approveMutation.isError || recalculateMutation.isError || markPaidMutation.isError) ? (
        <Card className="p-4 border-destructive/30 text-destructive">
          {publicErrorMessage(
            disputeMutation.error ?? approveMutation.error ?? recalculateMutation.error ?? markPaidMutation.error
          )}
        </Card>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">This month partner share</p>
          <p className="text-2xl font-bold">{centsToUsd(monthRevenue)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Pending settlement</p>
          <p className="text-2xl font-bold">
            {pendingSettlement ? centsToUsd(pendingSettlement.partner_share_cents) : '—'}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Last paid</p>
          <p className="text-2xl font-bold">
            {lastPaid ? centsToUsd(lastPaid.partner_share_cents) : '—'}
          </p>
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="font-medium mb-3">Transactions</h3>
        {transactionsQuery.isLoading ? (
          <Spinner />
        ) : transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No revenue transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Buyer</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Revenue</th>
                  <th className="py-2 pr-4">Share</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tr: RevenueTransaction) => (
                  <tr key={tr.id} className="border-b border-gray-50">
                    <td className="py-2 pr-4">{new Date(tr.created_at).toLocaleDateString()}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{tr.buyer_tenant_id}</td>
                    <td className="py-2 pr-4">{tr.transaction_type}</td>
                    <td className="py-2 pr-4">{centsToUsd(tr.revenue_cents)}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{tr.share_rate}</td>
                    <td className="py-2 pr-4">
                      <Badge variant="outline">{tr.settlement_id ? 'settled' : 'unsettled'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-medium mb-3">Settlements</h3>
        {settlementsQuery.isLoading ? (
          <Spinner />
        ) : settlements.length === 0 ? (
          <p className="text-sm text-muted-foreground">No settlements yet.</p>
        ) : (
          <div className="space-y-3">
            {settlements.map((s: Settlement) => (
              <div key={s.id} className="border rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">
                      {s.period_year}-{String(s.period_month).padStart(2, '0')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.transaction_count} transactions · {centsToUsd(s.partner_share_cents)} partner share
                    </p>
                    {s.dispute_reason ? (
                      <p className="text-xs text-red-600 mt-1">{s.dispute_reason}</p>
                    ) : null}
                    {s.ar_payout_ref ? (
                      <p className="text-xs text-muted-foreground mt-1 font-mono">payout: {s.ar_payout_ref}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={STATUS_BADGE_CLASS[s.status] ?? ''}>
                      {s.status}
                    </Badge>
                    {s.status === 'pending_review' ? (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => approveMutation.mutateAsync(s.id)}
                          disabled={approveMutation.isPending}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDisputingId(disputingId === s.id ? null : s.id)}
                        >
                          Dispute
                        </Button>
                      </>
                    ) : null}
                    {s.status === 'approved' ? (
                      <>
                        <input
                          className="border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 text-xs w-40"
                          placeholder="AR payout ref"
                          value={payoutRef}
                          onChange={(e) => setPayoutRef(e.target.value)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => markPaidMutation.mutateAsync({ settlementId: s.id, arPayoutRef: payoutRef })}
                          disabled={!payoutRef || markPaidMutation.isPending}
                        >
                          Mark Paid
                        </Button>
                      </>
                    ) : null}
                    {s.status === 'disputed' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => recalculateMutation.mutateAsync({ settlementId: s.id })}
                        disabled={recalculateMutation.isPending}
                      >
                        Recalculate
                      </Button>
                    ) : null}
                  </div>
                </div>
                {disputingId === s.id ? (
                  <div className="mt-2 flex gap-2">
                    <input
                      className="flex-1 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 text-sm"
                      placeholder="Dispute reason"
                      value={disputeReason}
                      onChange={(e) => setDisputeReason(e.target.value)}
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDispute(s.id)}
                      disabled={!disputeReason || disputeMutation.isPending}
                    >
                      Submit Dispute
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
